import { useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { getClient } from 'lane-shared/apollo';
import { channelsByModuleCategoriesQuery } from 'graphql-queries';

export type PropertyChannel = {
  _id: string;
  name: string;
  profile?: {
    image?: string | null;
    logo?: string | null;
  } | null;
  address?: {
    street1?: string | null;
    city?: string | null;
    state?: string | null;
    code?: string | null;
  } | null;
};

type Result = {
  channels: PropertyChannel[];
  loading: boolean;
  total: number;
};

const PER_PAGE = 100;

export function useChannelsByModuleCategories(moduleCategory: string): Result {
  const [allChannels, setAllChannels] = useState<PropertyChannel[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const fetchedForCategory = useRef<string | null>(null);

  const { data, loading, fetchMore } = useQuery(channelsByModuleCategoriesQuery, {
    variables: {
      moduleCategories: [moduleCategory],
      pagination: { start: 0, perPage: PER_PAGE },
    },
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const firstPage: PropertyChannel[] =
    data?.me?.channelsByModuleCategories?.channels ?? [];
  const total: number =
    data?.me?.channelsByModuleCategories?.pageInfo?.total ?? 0;

  useEffect(() => {
    if (loading || total === 0 || firstPage.length === 0) return;
    if (fetchedForCategory.current === moduleCategory) return;

    fetchedForCategory.current = moduleCategory;

    if (total <= PER_PAGE) {
      setAllChannels(firstPage);
      return;
    }

    setIsFetchingMore(true);
    const pageCount = Math.ceil(total / PER_PAGE);
    const remainingFetches = Array.from({ length: pageCount - 1 }, (_, i) =>
      fetchMore({
        variables: {
          pagination: { start: (i + 1) * PER_PAGE, perPage: PER_PAGE },
        },
      })
    );

    Promise.all(remainingFetches)
      .then(results => {
        const more = results.flatMap(
          r => r.data?.me?.channelsByModuleCategories?.channels ?? []
        );
        setAllChannels([...firstPage, ...more]);
      })
      .catch(error => {
        console.error('Failed to fetch additional channels:', error);
        setAllChannels(firstPage);
      })
      .finally(() => setIsFetchingMore(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, total, moduleCategory]);

  const isReady = allChannels.length > 0 || (!loading && total === 0);

  return {
    channels: allChannels,
    loading: loading || isFetchingMore || !isReady,
    total,
  };
}
